import { makeStyles } from "@material-ui/core";
import { difference } from "lodash";
import React, { useEffect, useState } from "react";
import { createUseStyles } from "react-jss";

import useProjectData from "../../projects/scanners/data/project";

import { useParams } from "react-router-dom";
import DisclaimerProcessor from "../../helpers/DisclaimerProcessor";
import { isIdBundle, set_page_title } from "../../helpers/Functions";
import { report_pageview } from "../../helpers/GA";
import { bundleVerify } from "../../helpers/generator/Amplify";
import { collectTableData, formatTableData } from "../../helpers/Tables";
import { useQuery } from "../../ProjectValidator";
import CompareTable from "./CompareTable";
import FooterSection from "./FooterSection";
import { tabletMinWidth } from "./helpers/constants";
import HeroSection from "./HeroSection";
import LandingTopLinks from "./LandingTopLinks";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    "& main": {
      marginTop: `${theme.spacing(0)}px !important`
    },
    "& a": {
      color: theme.palette.gray.main,
      fontFamily: "Helvetica Neue LT W05_77 Bd Cn",
      textDecoration: "none"
    }
  },
  main: {
    flex: "1",
    backgroundColor: theme.palette.background.default,
    padding: `${theme.spacing(8)}px ${theme.spacing(4)}px`
  },
  [`@media only screen and (min-width: ${tabletMinWidth}px)`]: {
    root: {
      height: "100vh"
    }
  }
}));

const useGlobalStyles = createUseStyles({
  "@global": {
    "@import":
      'url("//fast.fonts.net/t/1.css?apiType=css&projectid=3191221b-da14-4d41-8255-9e5f0837bd4b")',
    "@font-face": [
      {
        fontFamily: "Helvetica Neue LT W05_47 Lt Cn",
        src: 'url("/fonts/wf-pro-hc/083e1179-98b4-4cbd-a13a-50a7c78d6fea.woff2") format("woff2"), url("/fonts/wf-pro-hc/239170e6-7f0d-4c35-97d4-490b362e4ef7.woff") format("woff")'
      },
      {
        fontFamily: "Helvetica Neue LT W05_57 Cond",
        src: 'url("/fonts/pos/9516ef74-4fe9-45fe-a09f-493cb9518f71.woff2") format("woff2"),url("/fonts/pos/5b99bc59-1b2e-446b-961e-2e1dbdfcd641.woff") format("woff")'
      },
      {
        fontFamily: "Helvetica Neue LT W05_77 Bd Cn",
        src: 'url("/fonts/wf-pro-hc/02980b3d-1e37-4378-a5c6-4cf0d6ce0ba5.woff2") format("woff2"), url("/fonts/wf-pro-hc/2d1e8a5f-eb7a-47a1-be1d-878bb9a46b6e.woff") format("woff")'
      },
      {
        fontFamily: "Trade Gothic LT W01 Bold",
        src: 'url("/fonts/pos/f9be987b-0ea1-4c6e-9302-b14707c2a1a2.woff2") format("woff2"),url("/fonts/pos/b27d3ee2-b9fb-47e7-8eab-f22f15953932.woff") format("woff")'
      }
    ],
    // This is an override
    main: {
      marginTop: "0px"
    }
  },
  "*": {
    fontFamily: "Helvetica Neue LT W05_57 Cond"
  }
});

function LandingPreview(props) {
  const { project } = props;
  //eslint-disable-next-line
  const [{ projectData, pdLoading, pdError }, { loadLanding }] =
    useProjectData(project);

  const [overline, setOverline] = useState("");
  const [title, setTitle] = useState("");
  const [title2, setTitle2] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [photo_url, setPhotoUrl] = useState("");

  const [primary_brand, setPrimaryBrand] = useState("");
  const [primary_product_brand, setPrimaryProductBrand] = useState("");

  const [table, setTableData] = useState({});
  const [disclaimer_processor, setDisclaimerProcessor] = useState();

  const [logo_url, setLogoUrl] = useState("");
  const [pimrary_product_loaded, setPrimaryProductLoaded] = useState(false);
  const [secondary_products_loaded, setSecondarysProductLoaded] =
    useState(false);
  const [selected_locale, setSelectedLocale] = useState("");
  const [topLink, setTopLink] = useState({});
  const [qclSpe, setQclSpe] = useState(null);

  let classes = useStyles();
  useGlobalStyles();

  const { bundleId, id0, id1, id2, id3 } = useParams();
  const query = useQuery();
  const langquery = query.get("lang");
  // Verify whether the conditional "bundleId" is a product ID or a bundleId
  const bundleIdIsProductId = !isIdBundle(bundleId);
  var productId0, productId1, productId2, productId3;
  if (bundleIdIsProductId) {
    // Here we use the first four parameters, ignoring the last one
    productId0 = bundleId;
    productId1 = id0;
    productId2 = id1;
    productId3 = id2;
  } else {
    productId0 = id0;
    productId1 = id1;
    productId2 = id2;
    productId3 = id3;
  }

  // Difference function ensures that the primary product is not included in the secondary array
  var secondary_prod_ids = difference(
    [productId1, productId2, productId3],
    [productId0]
  );
  // var primaryProductFilters = `filter[id][in]=${productId0}&filter[status][eq]=active&filter[brand_family.brand.id][eq]=1`;
  // const secondary_ids = secondary_prod_ids.join(",");
  // var secondaryProductsFilters = `filter[id][in]=${secondary_ids}&filter[status][eq]=active`;
  const product_ids = [productId0, ...secondary_prod_ids];

  const init = () => {
    // const landing_data = new LandingData(project, project_loader, collections_config)
    loadLanding(product_ids).then((results) => {
      const { collections, collections_maps: maps, config } = results;
      const { global_settings, quick_compare_landing } = collections;
      let loaded_db_data = collections;
      const foundProducts = product_ids.map((pid) => maps.products[pid]);

      //Product Data
      const primaryProduct = foundProducts[0];
      const secondaryProducts = foundProducts.slice(1).filter((v) => !!v);
      setPrimaryProductLoaded(!!primaryProduct);
      if (primaryProduct === undefined) {
        return;
      }

      setSecondarysProductLoaded(secondaryProducts.length > 0);

      //Used for deciding if primary product match primary brand
      setPrimaryProductBrand(primaryProduct.brand.id);
      setPrimaryBrand(global_settings.primary_brand.id);

      //Disclaimers
      const disclaimer_processor = new DisclaimerProcessor(
        maps.disclaimers,
        "quick-compare-landing"
      );
      setDisclaimerProcessor(disclaimer_processor);

      //Make sure locale exists
      const selectedLocale =
        collections.locales.find(
          (locale) => locale.language.toLowerCase() === langquery?.toLowerCase()
        ) || global_settings?.default_locale;
      setSelectedLocale(selectedLocale.language);

      //Hero Section
      const primary_product_brand_name = primaryProduct?.brand.brand;
      const primary_product_model = primaryProduct?.model;
      const primary_product_variant = primaryProduct?.variant;
      setTitle(primary_product_model);
      setTitle2(primary_product_variant);
      setOverline(`${primary_product_brand_name} ${primaryProduct?.family}`);
      setPhotoUrl(
        primaryProduct?.getLocalized("product_image", selected_locale)
      );
      setDescription(
        disclaimer_processor.ps(
          primaryProduct?.getLocalized("description", selected_locale)
        )
      );
      // setDescription(disclaimer_processor.ps(primaryProduct?.description));
      // setPhotoUrl(primaryProduct?.image_url);

      let prev_brand = null;
      let prev_family = null;
      let secondary_product_names = secondaryProducts.map((product) => {
        let brand_string = "";
        if (product.brand && prev_brand !== product.brand.id) {
          prev_brand = product.brand.id;
          brand_string = product.brand.brand;
        }

        let family_string = "";
        if (product.family && prev_family !== product.family) {
          prev_family = product.family;
          family_string = product.family;
        }

        return (
          `${brand_string} ${family_string} ${product.model} ${product.variant}`
            // Remove excess whitespace and trim
            .replace(/ +/g, " ")
            .trim()
        );
      });

      let competing_products = secondary_product_names.join(", ");

      const subtitle = `vs. ${competing_products}`;
      setSubtitle(subtitle);

      let table_config = {
        primary_product: primaryProduct,
        secondary_products: secondaryProducts,

        attributes: quick_compare_landing.enabled_attributes,
        price_types: quick_compare_landing.enabled_price_types
      };
      var tableData = collectTableData(
        table_config,
        selectedLocale,
        loaded_db_data
      );
      formatTableData(tableData, disclaimer_processor);
      setTableData(tableData);

      //Page Title
      const documentTitle = global_settings.getLocalized(
        "page_title",
        selectedLocale?.language
      );
      set_page_title(
        `Compare ${primary_product_brand_name} ${primary_product_model} ${subtitle} - ${documentTitle}`
      );

      //Footer
      setLogoUrl(loaded_db_data.global_settings.logo_url);
      const export_link_label = global_settings?.getLocalized(
        "export_link_label",
        selected_locale
      );
      const export_link_icon_url =
        global_settings?.getLocalized(
          "export_link_icon_url",
          selected_locale
        ) || global_settings.export_link_icon_url;
      const product_ids_str = product_ids.reduce((str, id) => {
        if (id) str += `${id}/`;
        return str;
      }, "");
      const export_link_url = `/${project}/qcl-spe/${product_ids_str}?lang=${selected_locale}`;
      setTopLink({
        export_link_label,
        export_link_url,
        export_link_icon_url
      });
      setQclSpe(config?.available_features?.quick_compare_landing_spe);

      //Analytics
      report_pageview();
    });
  };

  useEffect(() => {
    if (bundleIdIsProductId) {
      init();
      props.setToolbarData({});
    } else {
      // Bundle ID is present - validate
      bundleVerify(bundleId, props.project)
        .then(({ valid = false }) => {
          if (valid) init();
        })
        .finally(() => {
          props.setToolbarData({});
        });
    }
    //eslint-disable-next-line
  }, [selected_locale]);

  if (
    !disclaimer_processor ||
    !pimrary_product_loaded ||
    !secondary_products_loaded ||
    primary_product_brand === "" ||
    primary_product_brand !== primary_brand
  ) {
    return <React.Fragment />;
  }

  return (
    <div className={classes.root}>
      {qclSpe && <LandingTopLinks topLink={topLink} />}
      <HeroSection
        photo_url={photo_url}
        overline={overline}
        title={title}
        title2={title2}
        subtitle={subtitle}
        description={description}
      />
      <main className={classes.main}>
        <CompareTable
          table={table}
          disclaimer_processor={disclaimer_processor}
        />
      </main>
      <FooterSection
        disclaimer_processor={disclaimer_processor}
        logo_url={logo_url}
        selected_locale={selected_locale}
      />
    </div>
  );
}

export default LandingPreview;
